<script setup lang="ts" generic="T extends string">
/**
 * An inspector's settings in tabs, for a kind with more to set than one
 * screen holds: a few groups, one showing at a time, so nothing is found by
 * scrolling past everything else.
 *
 * Drawn to be seen at a glance and not taken for a setting: a strip across
 * the panel, each tab a pictogram over its name, the open one lifted in the
 * accent. The strip stays pinned at the top of the panel while its group
 * scrolls under it. Each group is a slot named after its tab. Which tab is
 * open is the caller's (see `useRememberedTab`), so it can open the one that
 * matters, such as an element's settings the moment one is picked on the page.
 */
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui'
import type { InspectorTab } from '@/sections/inspectorTabs'

const props = defineProps<{
  tabs: readonly InspectorTab<T>[]
  /** What the tabs are, for a screen reader. */
  label: string
}>()

const open = defineModel<T>('open', { required: true })

/** The tab chosen, one of these: the kit's tabs say which by its plain name. */
function choose(value: string | undefined): void {
  const chosen = props.tabs.find((tab) => tab.id === value)
  if (chosen) open.value = chosen.id
}
</script>

<template>
  <Tabs :model-value="open" variant="underline" class="inspector-tabs" @update:model-value="choose">
    <TabsList class="inspector-tabs__list" :label="label">
      <TabsTrigger
        v-for="tab in tabs"
        :key="tab.id"
        :value="tab.id"
        class="inspector-tabs__tab"
        :data-tab="tab.id"
      >
        <svg v-if="tab.icon" class="inspector-tabs__icon" viewBox="0 0 16 16" aria-hidden="true">
          <path
            v-for="(d, at) in tab.icon"
            :key="at"
            :d="d"
            fill="none"
            stroke="currentColor"
            stroke-width="1.35"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span class="inspector-tabs__name">
          {{ tab.label }}
          <span v-if="tab.count !== undefined" class="inspector-tabs__count">{{ tab.count }}</span>
        </span>
      </TabsTrigger>
    </TabsList>
    <TabsContent v-for="tab in tabs" :key="tab.id" :value="tab.id" class="inspector-tabs__panel">
      <slot :name="tab.id" />
    </TabsContent>
  </Tabs>
</template>

<style scoped>
/* A strip across the full width of the panel, pinned to the top of its
   scroll, the panel's own padding above it covered so nothing shows through.
   The panel says how much padding that is, when it is not the inspector's. */
.inspector-tabs {
  --inset-x: var(--tabs-inset-x, var(--sp-5));
  --inset-top: var(--tabs-inset-top, var(--sp-6));
}

.inspector-tabs .inspector-tabs__list {
  position: sticky;
  top: calc(-1 * var(--inset-top));
  z-index: 3;
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-flow: column;
  gap: 2px;
  margin: 0 calc(-1 * var(--inset-x));
  padding: var(--sp-2) var(--sp-3) 0;
  border-bottom: 1px solid var(--hairline-strong);
  background: var(--panel-hover);
  box-shadow: 0 calc(-1 * var(--inset-top)) 0 var(--panel-hover);
}

.inspector-tabs__list .inspector-tabs__tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-width: 0;
  margin-bottom: -1px;
  padding: 7px var(--sp-2) 6px;
  border: 1px solid transparent;
  border-bottom: 0;
  border-radius: var(--radius) var(--radius) 0 0;
  color: var(--ink-soft);
  font-size: var(--text-xs);
  font-weight: 600;
  transition:
    background var(--quick),
    color var(--quick);
}

.inspector-tabs__list .inspector-tabs__tab:hover:not(.is-active) {
  background: color-mix(in srgb, var(--panel) 55%, transparent);
  color: var(--ink);
}

/* The open tab: lifted out of the strip onto the panel, in the accent, a
   firm line under its name. */
.inspector-tabs__list .inspector-tabs__tab.is-active {
  border-color: var(--hairline-strong);
  background: var(--panel);
  box-shadow: inset 0 -2px 0 var(--accent-fill);
  color: var(--accent);
}

.inspector-tabs__list .inspector-tabs__tab:focus-visible {
  outline: none;
  box-shadow:
    inset 0 -2px 0 var(--accent-fill),
    0 0 0 2px var(--accent-line);
}

.inspector-tabs__icon {
  width: 17px;
  height: 17px;
  flex: none;
}

.inspector-tabs__name {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
}

.inspector-tabs__count {
  min-width: 17px;
  padding: 0 4px;
  border-radius: 999px;
  background: var(--accent-quiet);
  color: var(--accent);
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  line-height: 15px;
  text-align: center;
}

.inspector-tabs .inspector-tabs__panel {
  padding-top: var(--sp-6);
}

.inspector-tabs .inspector-tabs__panel:focus-visible {
  box-shadow: none;
}
</style>
